import React, { FC, useEffect, useState } from 'react';
import { NumberFormatValues } from 'react-number-format';
import { formatFilterPrice, Title } from '@investoro/core';
import styled from 'styled-components';
import { CalculatorInputType, CalculatorSettings } from 'utils/types/Calculator';

import CalculatorTable, { CalculatorSection } from 'components/calculator/CalculatorTable';
import { StyledNumeric } from 'components/calculator/StyledInputs';
import { BoxContainer } from 'components/common/BoxContainer';
import CardsGrid, { CardItem } from 'components/common/CardsGrid';

const BoxContent = styled.div`
    display: flex;
    align-content: center;
    width: 100%;
`;

const BoxContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const ResetButton = styled.span`
    margin-top: 20px;
    font-weight: var(--font-weight);
    font-size: 14px;
    line-height: 20px;
    color: var(--color-primary);
    cursor: pointer;
    text-decoration-line: underline;
    display: flex;
    justify-content: flex-end;
    padding-bottom: 35px;

    &:hover {
        color: var(--color-alt);
    }
`;

export interface FlipCosts {
    notary?: number;
    broker?: number;
    maintenanceFees?: number;
    rentFees?: number;
    saleBroker?: number;
    tax?: number;
    portalFees?: number;
    photographerAmount?: number;
    userOption?: number;
    sumOfCosts?: number;
    renovationFrom1To30Amount?: number;
    renovationFrom30To40Amount?: number;
    renovationFrom40To50Amount?: number;
    renovationFrom50To60Amount?: number;
    renovationFrom60PlusAmount?: number;
}

interface FlipResult {
    isIndividualFlip?: boolean;
    maxPurchasePrice?: number;
    margin?: number;
    profit?: number;
    realPrice?: number;
    certainPrice?: number;
    flipCosts?: FlipCosts;
}

export interface CalculationsTabsProps {
    id: number;
    calculation: boolean;
    flipInfo?: FlipResult;
    currentPrice?: number;
    area?: number;
    calculatorState: CalculatorSettings;
    onValueChange: (fieldName: keyof CalculatorSettings, value: string) => void;
    onUpdateField: (fieldName: keyof CalculatorSettings, value: string) => Promise<void>;
    onUpdateTotalField?: (fieldName: keyof CalculatorSettings, value: string) => Promise<void>;
    onResetField: (fieldName: keyof CalculatorSettings) => Promise<void>;
    onResetAll: () => Promise<void>;
    loadFlipCalculations: () => void;
    hasCalculationRole: boolean;
}

const CalculationsTab: FC<CalculationsTabsProps> = ({ id, hasCalculationRole, flipInfo, currentPrice, area, calculatorState, onValueChange, onUpdateField, onUpdateTotalField, onResetField, onResetAll, loadFlipCalculations }) => {
    const [certainPrice, setCertainPrice] = useState<string>(flipInfo?.certainPrice?.toString() || '');
    const [isEdited, setIsEdited] = useState(false);
    const canEditTotalFields = flipInfo && flipInfo.certainPrice;

    const initialTotalValues: Record<string, string> = {
        flipTime: calculatorState?.flipTime?.toString() || '',
        minProfitAmount: calculatorState?.minProfitAmount?.toString() || '',
        minProfitPercent: flipInfo?.profit?.toString() || '',
        notary: flipInfo?.flipCosts?.notary?.toString() || '',
        brokerPercent: flipInfo?.flipCosts?.broker?.toString() || '',
        rentFees: flipInfo?.flipCosts?.rentFees?.toString() || '',
        maintenanceFees: flipInfo?.flipCosts?.maintenanceFees?.toString() || '',
        portalFees: flipInfo?.flipCosts?.portalFees?.toString() || '',
        photographerAmount: flipInfo?.flipCosts?.photographerAmount?.toString() || '',
        saleBrokerPercent: flipInfo?.flipCosts?.saleBroker?.toString() || '',
        // @ts-expect-error after update API error will disappear
        pcc: flipInfo?.flipCosts?.pcc?.toString() || '',
        userOption: flipInfo?.flipCosts?.userOption?.toString() || '',
        renovationFrom1To30Amount: flipInfo?.flipCosts?.renovationFrom1To30Amount?.toString() || '',
        renovationFrom30To40Amount: flipInfo?.flipCosts?.renovationFrom30To40Amount?.toString() || '',
        renovationFrom40To50Amount: flipInfo?.flipCosts?.renovationFrom40To50Amount?.toString() || '',
        renovationFrom50To60Amount: flipInfo?.flipCosts?.renovationFrom50To60Amount?.toString() || '',
        renovationFrom60PlusAmount: flipInfo?.flipCosts?.renovationFrom60PlusAmount?.toString() || ''
    };

    useEffect(() => {
        loadFlipCalculations();
    }, [id]);

    useEffect(() => {
        if (flipInfo && flipInfo.certainPrice) {
            setCertainPrice(flipInfo.certainPrice.toString());
        }
    }, [flipInfo]);

    const handleCertianPriceChange = async (newValue: string) => {
        setCertainPrice(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    const handleCertainPriceBlur = async () => {
        setIsEdited(true);
        await onUpdateField('certainPrice', certainPrice);
        setIsEdited(false);
    };

    const hasCertainPrice = !!flipInfo?.certainPrice;

    const calculationsTop: CardItem[] = [
        ...currentPrice
            ? [
                {
                    label: 'Maksymalna cena zakupu',
                    value: formatFilterPrice(flipInfo?.maxPurchasePrice?.toString()),
                    suffix: 'zł',
                    showStar: true,
                    helperText: !hasCertainPrice ? 'Aby wyświetlić kalkulacje, podaj cenę po remoncie' : undefined
                },
                {
                    label: 'Aktualna cena',
                    value: formatFilterPrice(currentPrice.toString()),
                    suffix: 'zł',
                    showStar: true
                }
            ]
            : [],
        {
            label: 'Cena pewniak po remoncie',
            content: <StyledNumeric
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e)}
                isNotInTable
                value={certainPrice}
                isEdited={isEdited}
                onValueChange={(values: NumberFormatValues) => handleCertianPriceChange(values.value)}
                onBlur={handleCertainPriceBlur}
                thousandSeparator=" "
                decimalScale={0}
                allowNegative={false}
                suffix={' zł'}
                fontSize={'18px'}
                isAllowed={(values: NumberFormatValues) => {
                    if (values.value === '') return true;
                    if (values.value.length > 1 && values.value.startsWith('0')) return false;

                    return true;
                }}
            />,
            showStar: true
        }
    ];

    const calculationsBottom = [
        {
            label: 'Zysk',
            value: formatFilterPrice(flipInfo?.profit?.toString()),
            suffix: 'zł',
            showStar: true
        },
        {
            label: 'Koszty',
            value: formatFilterPrice(flipInfo?.flipCosts?.sumOfCosts),
            suffix: 'zł',
            showStar: true
        },
        {
            label: 'Marża',
            value: flipInfo?.margin?.toString(),
            suffix: '%',
            showStar: true
        }
    ];

    const calculatorStructure: CalculatorSection[] = [
        {
            title: 'Podstawowe parametry',
            fields: [
                {
                    title: 'Maksymalny czas trwania flipa',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'flipTime',
                    value: calculatorState?.flipTime || '',
                    min: 1,
                    max: 36,
                    suffix: 'miesięcy',
                    result: calculatorState?.flipTime || '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'miesięcy'
                }
            ]
        },
        {
            title: 'Zysk',
            fields: [
                {
                    title: 'Minimalna kwota zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitAmount',
                    value: calculatorState?.minProfitAmount || '',
                    min: 1,
                    suffix: 'zł',
                    result: `${formatFilterPrice(calculatorState?.minProfitAmount)} zł` || '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Minimalny procent zysku',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'minProfitPercent',
                    value: calculatorState.minProfitPercent,
                    min: 0,
                    max: 100,
                    suffix: '%',
                    result: flipInfo && flipInfo.profit ? `${formatFilterPrice(flipInfo.profit.toString()) as string} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                }
            ]
        },
        {
            title: 'Koszty',
            fields: [
                {
                    title: 'Notariusz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'notary',
                    value: calculatorState.notary,
                    min: 0,
                    max: 100000,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.notary ? `${formatFilterPrice(flipInfo.flipCosts.notary.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Taksa',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'tax',
                    value: '',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.tax ? `${formatFilterPrice(flipInfo.flipCosts.tax.toString())} zł` : '',
                    readonly: true
                },
                {
                    title: 'Pośrednik przy zakupie mieszkania',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'brokerPercent',
                    value: calculatorState.brokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '%',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.broker ? `${formatFilterPrice(flipInfo.flipCosts.broker.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Opłaty eksploatacyjne miesięczne za m2',
                    helperText: 'czynsz',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'rentFees',
                    value: calculatorState.rentFees,
                    min: 0,
                    max: 10000,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.rentFees ? `${formatFilterPrice(flipInfo.flipCosts.rentFees.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Media miesięczne',
                    helperText: 'prąd, gaz podatek od nieruchomości',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'maintenanceFees',
                    value: calculatorState.maintenanceFees,
                    min: 0,
                    max: 1000,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.maintenanceFees ? `${formatFilterPrice(flipInfo.flipCosts.maintenanceFees.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Ogłoszenia na portalach',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'portalFees',
                    value: calculatorState.portalFees,
                    min: 0,
                    max: 2000,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.portalFees ? `${formatFilterPrice(flipInfo.flipCosts.portalFees.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Fotograf',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'photographerAmount',
                    value: calculatorState.photographerAmount,
                    min: 0,
                    max: 1000,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.photographerAmount ? `${formatFilterPrice(flipInfo.flipCosts.photographerAmount.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Pośrednik do sprzedaży',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'saleBrokerPercent',
                    value: calculatorState.saleBrokerPercent,
                    min: 0,
                    max: 100,
                    suffix: '%',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.saleBroker ? `${formatFilterPrice(flipInfo.flipCosts.saleBroker.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                {
                    title: 'Podatek PCC',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'pcc',
                    value: calculatorState.pcc,
                    min: 0,
                    max: 100,
                    suffix: '%',
                    // @ts-expect-error after update API error will disappear
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.pcc ? `${formatFilterPrice(flipInfo.flipCosts.pcc.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                },
                ...flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom1To30Amount
                    ? [
                        {
                            title: 'Remont do 30 m2',
                            type: CalculatorInputType.NUMBER,
                            fieldName: 'renovationFrom1To30Amount' as keyof CalculatorSettings,
                            value: calculatorState.renovationFrom1To30Amount,
                            min: 0,
                            max: 2500,
                            suffix: 'zł',
                            result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom1To30Amount ? `${formatFilterPrice(flipInfo.flipCosts.renovationFrom1To30Amount.toString())} zł` : '',
                            resultIsEditable: canEditTotalFields ? true : false,
                            totalSuffix: 'zł'
                        }
                    ]
                    : [],
                ...flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom30To40Amount
                    ? [
                        {
                            title: 'Remont 30-40 m2',
                            type: CalculatorInputType.NUMBER,
                            fieldName: 'renovationFrom30To40Amount' as keyof CalculatorSettings,
                            value: calculatorState.renovationFrom30To40Amount,
                            min: 0,
                            max: 2500,
                            suffix: 'zł',
                            result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom30To40Amount ? `${formatFilterPrice(flipInfo.flipCosts.renovationFrom30To40Amount.toString())} zł` : '',
                            resultIsEditable: canEditTotalFields ? true : false,
                            totalSuffix: 'zł'
                        }
                    ]
                    : [],
                ...flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom40To50Amount
                    ? [
                        {
                            title: 'Remont 40-50 m2',
                            type: CalculatorInputType.NUMBER,
                            fieldName: 'renovationFrom40To50Amount' as keyof CalculatorSettings,
                            value: calculatorState.renovationFrom40To50Amount,
                            min: 0,
                            max: 2500,
                            suffix: 'zł',
                            result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom40To50Amount ? `${formatFilterPrice(flipInfo.flipCosts.renovationFrom40To50Amount.toString())} zł` : '',
                            resultIsEditable: canEditTotalFields ? true : false,
                            totalSuffix: 'zł'
                        }
                    ]
                    : [],
                ...flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom50To60Amount
                    ? [
                        {
                            title: 'Remont 50-60 m2',
                            type: CalculatorInputType.NUMBER,
                            fieldName: 'renovationFrom50To60Amount' as keyof CalculatorSettings,
                            value: calculatorState.renovationFrom50To60Amount,
                            min: 0,
                            max: 2500,
                            suffix: 'zł',
                            result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom50To60Amount ? `${formatFilterPrice(flipInfo.flipCosts.renovationFrom50To60Amount.toString())} zł` : '',
                            resultIsEditable: canEditTotalFields ? true : false,
                            totalSuffix: 'zł'
                        }
                    ]
                    : [],
                ...flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom60PlusAmount
                    ? [
                        {
                            title: 'Remont 60+ m2',
                            type: CalculatorInputType.NUMBER,
                            fieldName: 'renovationFrom60PlusAmount' as keyof CalculatorSettings,
                            value: calculatorState.renovationFrom60PlusAmount,
                            min: 0,
                            max: 2500,
                            suffix: 'zł',
                            result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.renovationFrom60PlusAmount ? `${formatFilterPrice(flipInfo.flipCosts.renovationFrom60PlusAmount.toString())} zł` : '',
                            resultIsEditable: canEditTotalFields ? true : false,
                            totalSuffix: 'zł'
                        }
                    ]
                    : [],
                {
                    title: 'Własna pozycja',
                    type: CalculatorInputType.NUMBER,
                    fieldName: 'userOption',
                    value: calculatorState.userOption ? `${calculatorState.userOption.toString()} zł` : '',
                    min: 0,
                    suffix: 'zł',
                    result: flipInfo && flipInfo.flipCosts && flipInfo.flipCosts.userOption ? `${formatFilterPrice(flipInfo.flipCosts.userOption.toString())} zł` : '',
                    resultIsEditable: canEditTotalFields ? true : false,
                    totalSuffix: 'zł'
                }
            ]
        }
    ];

    return (
        <>
            <BoxContainer>
                <TitleWrapper>
                    <Title>Kalkulacje</Title>
                </TitleWrapper>
            </BoxContainer>
            {!hasCalculationRole
                ? <BoxContainer>
                    <BoxContent>Opcja niedostępna w wybranym pakiecie.</BoxContent>
                </BoxContainer>
                : <>
                    <BoxContainer>
                        <BoxContent>
                            <BoxContentWrapper>
                                <CardsGrid items={calculationsTop} isSmallLabel />
                                {/* {flipInfo && !flipInfo.certainPrice ? <p>Aby dokonać kalkulacji podaj cenę po remoncie</p> : null} */}
                            </BoxContentWrapper>
                        </BoxContent>

                    </BoxContainer>
                    <BoxContainer>
                        <CalculatorTable
                            sections={calculatorStructure}
                            calculatorState={calculatorState}
                            area={area}
                            onValueChange={onValueChange}
                            onResetField={onResetField}
                            onUpdateField={onUpdateField}
                            onUpdateTotalField={onUpdateTotalField}
                            hasThirdColumn
                            initialTotalValues={initialTotalValues}
                        />
                        <ResetButton onClick={onResetAll}>
                            <span>Przywróć wartości domyślne</span>
                        </ResetButton>
                    </BoxContainer>
                    <BoxContainer>
                        <BoxContent>
                            <CardsGrid items={calculationsBottom} isSmallLabel />
                        </BoxContent>
                    </BoxContainer></>}
        </>
    );
};

export default CalculationsTab;
